import { reactive, computed } from 'vue'

export type SaveState = 'saving' | 'saved' | 'error'

export function useAutosave(
    save: (patch: Record<string, unknown>) => Promise<void>,
    onError?: (key: string, error: unknown) => void,
) {
    const states = reactive<Record<string, SaveState>>({})
    const latest = new Map<string, unknown>()
    const inflight = new Set<Promise<void>>()
    const queues = new Map<string, Promise<void>>()

    function saveField(key: string, value: unknown) {
        latest.set(key, value)
        states[key] = 'saving'
        const previous = queues.get(key) ?? Promise.resolve()
        const run = previous.then(async () => {
            try {
                await save({ [key]: value })
                if (latest.get(key) === value) states[key] = 'saved'
            } catch (error) {
                if (latest.get(key) === value) {
                    states[key] = 'error'
                    onError?.(key, error)
                }
            }
        })
        queues.set(key, run)
        inflight.add(run)
        run.finally(() => {
            inflight.delete(run)
            if (queues.get(key) === run) queues.delete(key)
        })
        return run
    }

    function retry(key: string) {
        return latest.has(key) ? saveField(key, latest.get(key)) : Promise.resolve()
    }

    function retryAll() {
        return Promise.all(Object.keys(states).filter(key => states[key] === 'error').map(retry))
    }

    async function flush() {
        while (inflight.size) await Promise.all([...inflight])
    }

    const hasErrors = computed(() => Object.values(states).includes('error'))
    const isSaving = computed(() => Object.values(states).includes('saving'))
    const hasSaved = computed(() => Object.keys(states).length > 0)

    return { states, saveField, retry, retryAll, flush, hasErrors, isSaving, hasSaved }
}
